/**
 * Input component
 *
 * Optional props:
 * disabled : boolean
 * width : string - width of toggle (with units)
 * meta : object - redux form meta data
 * input : object - redux form input data
 * onLabel : string - label to replace icon when button is checked
 * offLabel : string - label to replace icon when button is not checked
 *
 * Examples of component:
 * <Toggle />
 * <Toggle disabled />
 * <Toggle width="150px" onLabel="Active" offLabel="Inactive" />
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '../../variables';
import { InputMessage } from '../';
import onIcon from '../../../images/checked_white.svg';
import offIcon from '../../../images/error_white.svg';

const getColor = props => {
	if (props.disabled) return colors.grey.normal;
	if (props.checked) return colors.blue.digital;

	return colors.grey.graphite;
};

const CheckboxElement = styled.input`
	display: none;
`;

const ToggleElement = styled.label`
	position: relative;
	display: block;
	width: ${props => props.width || '60px'};
	overflow-x: hidden;
	background: ${props => getColor(props)};
	border-radius: 30px;
	cursor: pointer;
	-webkit-print-color-adjust: exact;
`;

const Handle = styled.span`
	position: absolute;
	height: 30px;
	width: 30px;
	background: #fff;
	border: 3px solid ${props => getColor(props)};
	border-radius: 100%;
	left: 50%;
	transform: translateX(-50%);
	-webkit-print-color-adjust: exact;
`;

const Slider = styled.div`
	width: calc(200% - 30px);
	position: relative;
	height: 30px;
	transform: ${props =>
		props.checked ? '0' : 'translateX(calc(-50% + 15px))'};
	text-transform: uppercase;
	transition: transform 0.2s ease-out;
`;

const Labels = styled.div`
	&:before, &:after {
		display: block;
		width: 50%;
		padding: 0 10px;
		height: 30px;
		line-height: 30px;
		overflow: hidden;
		background-repeat: no-repeat;
		color: #fff;
		-webkit-print-color-adjust: exact;

	}

	&:after {
		float: left;
		background-image: ${props => (props.onLabel ? '' : `url(${onIcon})`)};
		background-position: 10px center;
		background-size: 14px;
		content: '${props => props.onLabel}';
		-webkit-print-color-adjust: exact;

	}

	&:before {
		float: right;
		text-align: right;
		background-image: ${props => (props.offLabel ? '' : `url(${offIcon})`)};
		background-position: right 10px center;
		background-size: 14px;
		content: '${props => props.offLabel}';
		-webkit-print-color-adjust: exact;

	}
`;

class Toggle extends React.Component {
	render() {
		const { className, onLabel, offLabel, width, input, ...rest } = this.props;

		const meta = this.props.meta || {};
		const submitFailed = meta.submitFailed || false;
		const error = meta.error || false;

		// Normalize value to boolean to handle null, undefined, 0, 1, "true", "false", etc.
		const checked = Boolean(input.value);

		return (
			<div className={className}>
				<CheckboxElement type="checkbox" {...input} {...rest} />

				<ToggleElement
					{...input}
					{...rest}
					checked={checked}
					htmlFor={input.name}
					width={width}
				>
					<Slider checked={checked}>
						<Handle {...rest} checked={checked} />
						<Labels onLabel={onLabel} offLabel={offLabel} checked={checked} />
					</Slider>
				</ToggleElement>

				{submitFailed && error && <InputMessage>{error}</InputMessage>}
			</div>
		);
	}
}

export default Toggle;



// WEBPACK FOOTER //
// ./src/components/Common/Toggle/Toggle.js